import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { motion } from 'framer-motion';
import {
  FiEdit, FiTrash2, FiPlus, FiGrid,
  FiHome, FiTv, FiSettings, FiTruck,
  FiMap, FiFileText, FiAlertCircle, FiBook,
  FiWifi, FiActivity, FiThermometer, FiMove
} from 'react-icons/fi';
import { IoBedOutline } from 'react-icons/io5';
import { MdHotTub, MdDownhillSkiing } from 'react-icons/md';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { api } from '../../api';
import toast from 'react-hot-toast';

const iconOptions = [
  { value: 'home', label: 'Home', Icon: FiHome },
  { value: 'tv', label: 'TV', Icon: FiTv },
  { value: 'settings', label: 'Settings', Icon: FiSettings },
  { value: 'bed', label: 'Bed', Icon: IoBedOutline },
  { value: 'car', label: 'Car', Icon: FiTruck },
  { value: 'map', label: 'Map', Icon: FiMap },
  { value: 'list', label: 'List', Icon: FiFileText },
  { value: 'alert', label: 'Alert', Icon: FiAlertCircle },
  { value: 'book', label: 'Book', Icon: FiBook },
  { value: 'wifi', label: 'WiFi', Icon: FiWifi },
  { value: 'thermometer', label: 'Thermostat', Icon: FiThermometer },
  { value: 'thermostat', label: 'Thermostat', Icon: FiThermometer },
  { value: 'activity', label: 'Activities', Icon: FiActivity },
  { value: 'hot-tub', label: 'Hot Tub', Icon: MdHotTub },
  { value: 'skiing', label: 'Skiing', Icon: MdDownhillSkiing },
];

const CategoryModal = ({ category, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: category?.name || '',
    slug: category?.slug || '',
    icon: category?.icon || 'home',
    order_index: category?.order_index ?? 999,
    is_active: category?.is_active !== undefined ? category.is_active : true
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const SelectedIcon = iconOptions.find(opt => opt.value === formData.icon)?.Icon || FiHome;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-2xl font-display text-lodge-brown-800">
            {category ? 'Edit Category' : 'Create Category'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => {
                  const name = e.target.value;
                  setFormData({ 
                    ...formData, 
                    name,
                    slug: category ? formData.slug : generateSlug(name)
                  });
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-lodge-brown-500 focus:border-lodge-brown-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Slug
              </label>
              <input
                type="text"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-lodge-brown-500 focus:border-lodge-brown-500"
                pattern="[a-z0-9-]+"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Icon
              </label>
              <div className="grid grid-cols-3 gap-2">
                {iconOptions.map(({ value, label, Icon }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setFormData({ ...formData, icon: value })}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      formData.icon === value 
                        ? 'border-lodge-brown-600 bg-lodge-brown-50' 
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <Icon className="w-6 h-6 mx-auto mb-1" />
                    <div className="text-xs">{label}</div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Order
              </label>
              <input
                type="number"
                value={formData.order_index}
                onChange={(e) => setFormData({ ...formData, order_index: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-lodge-brown-500 focus:border-lodge-brown-500"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="mr-2"
              />
              <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                Active
              </label>
            </div>
          </div>

          <div className="flex justify-end space-x-4 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
            >
              {category ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AdminCategories = () => {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);

  const { data: categoriesResponse, isLoading } = useQuery('adminCategories', () => api.getCategories());
  const { data: sectionsResponse } = useQuery('allSections', () => api.getSections());
  const categories = categoriesResponse?.data || [];
  const sections = sectionsResponse?.data || [];
  
  // Calculate section counts for each category
  const sectionCounts = {};
  sections.forEach(section => {
    sectionCounts[section.category_id] = (sectionCounts[section.category_id] || 0) + 1;
  });

  const createMutation = useMutation(
    (data) => api.createCategory(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('adminCategories');
        toast.success('Category created successfully');
        setShowModal(false);
      },
      onError: () => toast.error('Failed to create category')
    }
  );

  const updateMutation = useMutation(
    ({ id, ...data }) => api.updateCategory(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('adminCategories');
        toast.success('Category updated successfully');
        setShowModal(false);
        setEditingCategory(null);
      },
      onError: () => toast.error('Failed to update category')
    }
  );

  const deleteMutation = useMutation(
    (id) => api.deleteCategory(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('adminCategories');
        toast.success('Category deleted successfully');
      },
      onError: () => toast.error('Failed to delete category')
    }
  );

  const reorderMutation = useMutation(
    (categories) => api.reorderCategories(categories),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('adminCategories');
        queryClient.invalidateQueries('categories');
        toast.success('Categories reordered successfully');
      },
      onError: () => toast.error('Failed to reorder categories')
    }
  );

  const handleSave = (data) => {
    if (editingCategory) {
      updateMutation.mutate({ id: editingCategory.id, ...data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setShowModal(true);
  };

  const handleDelete = (category) => {
    const sectionCount = sectionCounts[category.id] || 0;
    let confirmMessage;
    
    if (sectionCount > 0) {
      confirmMessage = `Are you sure you want to delete "${category.name}"?\n\nThis will permanently delete this category and its ${sectionCount} section${sectionCount !== 1 ? 's' : ''}.\n\nThis action cannot be undone.`;
    } else {
      confirmMessage = `Are you sure you want to delete "${category.name}"?`;
    }
    
    if (window.confirm(confirmMessage)) {
      deleteMutation.mutate(category.id);
    }
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(categories);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const categoriesWithNewOrder = items.map((cat, index) => ({
      id: cat.id,
      order_index: index
    }));

    reorderMutation.mutate(categoriesWithNewOrder);
  };

  const iconMap = {
    home: FiHome,
    tv: FiTv,
    settings: FiSettings,
    bed: IoBedOutline,
    car: FiTruck,
    map: FiMap,
    list: FiFileText,
    alert: FiAlertCircle,
    book: FiBook,
    'hot-tub': MdHotTub,
    skiing: MdDownhillSkiing,
  };

  return (
    <div>
      <div className="mb-6 flex justify-between items-center">
        <h1 className="text-3xl font-display text-lodge-brown-800">Manage Categories</h1>
        <button
          onClick={() => {
            setEditingCategory(null);
            setShowModal(true);
          }}
          className="btn-primary flex items-center"
        >
          <FiPlus className="mr-2" />
          Add Category
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lodge-brown-600"></div>
        </div>
      ) : categories.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <FiGrid className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No categories found</p>
        </div>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="categories-list">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="space-y-3 max-w-4xl"
              >
                {categories.map((category, index) => {
                  const Icon = iconMap[category.icon] || FiFileText;
                  return (
                    <Draggable
                      key={category.id}
                      draggableId={String(category.id)}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={snapshot.isDragging ? 'opacity-50' : ''}
                        >
                          <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className={`bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow ${
                              snapshot.isDragging ? 'shadow-2xl' : ''
                            }`}
                          >
                            <div className="p-4 flex items-center justify-between">
                              <div className="flex items-center space-x-4">
                                <div
                                  {...provided.dragHandleProps}
                                  className="cursor-move text-gray-400 hover:text-gray-600"
                                  title="Drag to reorder"
                                >
                                  <FiMove className="w-5 h-5" />
                                </div>
                                <div className="w-12 h-12 bg-lodge-brown-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                  <Icon className="w-6 h-6 text-lodge-brown-600" />
                                </div>
                                <div className="flex-grow">
                                  <div className="flex items-center space-x-3">
                                    <h3 className="text-lg font-semibold text-mountain-gray-800">
                                      {category.name}
                                    </h3>
                                    {sectionCounts[category.id] > 0 && (
                                      <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                                        {sectionCounts[category.id]} section{sectionCounts[category.id] !== 1 ? 's' : ''}
                                      </span>
                                    )}
                                    {sectionCounts[category.id] === 0 && (
                                      <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                                        No sections
                                      </span>
                                    )}
                                    <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
                                      category.is_active 
                                        ? 'text-green-800 bg-green-100' 
                                        : 'text-gray-800 bg-gray-100'
                                    }`}>
                                      {category.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                  </div>
                                  <div className="flex items-center space-x-4 text-sm text-mountain-gray-600 mt-1">
                                    <span className="font-mono">/{category.slug}</span>
                                    <span className="text-mountain-gray-500">Order: {category.order_index}</span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2 ml-4">
                                <button
                                  onClick={() => handleEdit(category)}
                                  className="p-2 text-lodge-brown-600 hover:text-lodge-brown-900 hover:bg-lodge-brown-50 rounded-lg transition-colors"
                                  title="Edit"
                                >
                                  <FiEdit className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDelete(category)}
                                  className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Delete"
                                >
                                  <FiTrash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </motion.div>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      {/* Modal */}
      {showModal && (
        <CategoryModal
          category={editingCategory}
          onClose={() => {
            setShowModal(false);
            setEditingCategory(null);
          }}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

export default AdminCategories;